import { Fragment, ReactNode } from 'react';

import { Box, Skeleton, Text } from '@rbilabs/universal-components';
import { useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import Currency from 'components/currency';
import { LogUserErrorSeverity, useLogUserErrorMessage } from 'hooks/log-user-error-message';
import { theme } from 'styles/configure-theme';
import { primitive } from 'styles/constants/primitives';

interface PaymentFooterContainerProps {
  CTAButton: ReactNode;
  totalValue: number;
  isLoading: boolean;
  showAddPaymentLabel?: boolean;
  hadMinimumCashAppError?: boolean;
}
const TotalContainer = Box.withConfig({
  mb: '$4',
  flexDirection: 'row',
  focusable: true,
});

export const AddPaymentLabel = Text.withConfig({
  variant: 'copyTwo',
  focusable: true,
  color: primitive.bk.$fieryRedDarken10,
  marginBottom: '$4',
});

export const GrandTotalLabel = Text.withConfig({
  flexGrow: 1,
  color: theme.token('text-default'),
  fontWeight: 'bold',
  focusable: true,
});

const ErrorMessage = () => {
  const { formatMessage } = useIntl();

  useLogUserErrorMessage({
    message: 'CashAppButton: ' + formatMessage({ id: 'sorryMinimumCashApp' }),
    severity: LogUserErrorSeverity.Recoverable,
  });

  return (
    <ErrorContainer>
      <ErrorText>{formatMessage({ id: 'sorryMinimumCashApp' })}</ErrorText>
    </ErrorContainer>
  );
};

export const ErrorContainer = Box.withConfig({
  marginBottom: '$6',
  paddingX: '$6',
  paddingY: '$2',
  paddingRight: '$6',
  alignItems: 'center',
});

export const ErrorText = Text.withConfig({
  color: primitive.bk.$alertRed,
  fontSize: 'xs',
  textAlign: 'center',
});

export const PaymentFooterContainer: React.FC<PaymentFooterContainerProps> = ({
  CTAButton,
  totalValue,
  isLoading,
  showAddPaymentLabel = false,
  hadMinimumCashAppError = false,
}) => {
  const { formatMessage } = useIntl();

  // <View style={styles.buttonContainer}>
  return (
    <View style={styles.buttonContainer}>
      {isLoading ? (
        <Skeleton width="1/4" height="$4" rounded={12} marginBottom="$2" />
      ) : (
        <Fragment>
          {hadMinimumCashAppError && <ErrorMessage />}
          {showAddPaymentLabel && (
            <AddPaymentLabel>{formatMessage({ id: 'mustAddPaymentMethod' })}</AddPaymentLabel>
          )}
          <TotalContainer>
            <GrandTotalLabel fontSize={'xl'} flexGrow={0}>
              {formatMessage({
                id: 'total',
              })}
              {': '}
            </GrandTotalLabel>
            <Currency
              testID="grand-total"
              amount={totalValue}
              isBold
              textProps={{
                color: theme.token('text-default'),
                margin: 0,
                flexGrow: 0,
                fontSize: 'xl',
              }}
            />
          </TotalContainer>
        </Fragment>
      )}
      {CTAButton}
    </View>
  );
};

const styles = StyleSheet.create({
  buttonContainer: {
    alignItems: 'center',
    width: '100%',
  },
});
